import React, { useState, useEffect } from "react";
import { ModalWrapper } from './ModalWrapper';
import { POPUP_STYLES, MODAL_WIDTHS } from '../../constants/popupStyles';
import { QuestionnaireAnswer } from "../../types/api";
import apiService from "../../services/api";

interface ViewQuestionnaireModalProps {
    isOpen: boolean;
    onClose: () => void;
    questionnaireId: number;
    questionnaireTitle: string;
    lang?: string;
}

export const ViewQuestionnaireModal: React.FC<ViewQuestionnaireModalProps> = ({
    isOpen,
    onClose,
    questionnaireId,
    questionnaireTitle,
    lang,
}) => {
    const [questions, setQuestions] = useState<QuestionnaireAnswer[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const token = localStorage.getItem("token") || "";

    // Text direction based on clinic language
    const textDirection = lang === "ENG" ? "ltr" : "rtl";

    useEffect(() => {
        const fetchQuestionnaireDetails = async () => {
            if (!isOpen || !questionnaireId) return;

            setIsLoading(true);
            setError(null);
            try {
                const data = await apiService.getQuestionnaireDetails({
                    token,
                    questionnaire_id: questionnaireId,
                });
                console.log("Questionnaire details response:", data);

                // Validate response structure
                if (data && Array.isArray(data.questions)) {
                    setQuestions(data.questions);
                } else {
                    console.error("API response doesn't have questions array:", data);
                    setError("Invalid response format from server");
                    setQuestions([]);
                }
            } catch (err: any) {
                console.error("Failed to load questionnaire details:", err);
                setError("Failed to load questionnaire details");
                setQuestions([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchQuestionnaireDetails();
    }, [isOpen, questionnaireId, token]);

    if (!isOpen) return null;

    return (
        <ModalWrapper modalId="ViewQuestionnaireModal"
            isOpen={isOpen}
            onClose={onClose}
            title={questionnaireTitle}
            isLoading={isLoading}
            width={MODAL_WIDTHS.xxxxl}
            className="max-h-[80vh] flex flex-col"
        >
            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto px-3 py-3 min-h-0">
                {isLoading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#366A75] mx-auto"></div>
                            <p className="text-[#366A75] mt-4 text-sm">Loading questionnaire...</p>
                        </div>
                    </div>
                ) : error ? (
                    <div className="text-center py-12">
                        <p className="text-red-600">{error}</p>
                    </div>
                ) : (
                    <div className="space-y-3" dir={textDirection}>
                        {Array.isArray(questions) && questions.length > 0 ? (
                            questions.map((qa, index) => (
                                <div key={index} className="bg-[#EBF5F5] rounded-lg p-3">
                                    {/* Question */}
                                    <div className="mb-2">
                                        <p className={`font-semibold text-[#366A75] ${textDirection === 'ltr' ? 'text-left' : 'text-right'}`}>{qa.text}</p>
                                    </div>
                                    {/* Answers */}
                                    <div className="space-y-1">
                                        {qa.answers.map((answer: string, answerIndex: number) => (
                                            <p key={answerIndex} className={`text-[#366A75] ${textDirection === 'ltr' ? 'text-left pl-0' : 'text-right pl-4'}`}>
                                                {answer}
                                            </p>
                                        ))}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="text-center py-8 text-[#366A75]/60">
                                <p className="text-sm">No questionnaire data available</p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Modal Footer */}
            <div className="px-4 py-3 flex justify-end flex-shrink-0">
                <button
                    onClick={onClose}
                    className={POPUP_STYLES.button}
                >
                    Close
                </button>
            </div>
        </ModalWrapper>
    );
};
